import type { RsvpStatus } from "@/lib/api/rsvp";

export interface RsvpLike {
  id: string;
  status: RsvpStatus;
  created_at?: string;
}

/** Minimum confirmed players required (default 70% of max). */
export function computeMinPlayers(maxPlayers: number, minConfirmPct = 70): number {
  if (maxPlayers <= 0) return 0;
  return Math.ceil((maxPlayers * minConfirmPct) / 100);
}

export function countGoing(rsvps: Pick<RsvpLike, "status">[]): number {
  return rsvps.filter((r) => r.status === "going").length;
}

export function countReserves(rsvps: Pick<RsvpLike, "status">[]): number {
  return rsvps.filter((r) => r.status === "reserve").length;
}

export function isMatchConfirmed(goingCount: number, minPlayers: number): boolean {
  return goingCount >= minPlayers;
}

export function canAddReserve(rsvps: Pick<RsvpLike, "status">[], maxReserves = 2): boolean {
  return countReserves(rsvps) < maxReserves;
}

/**
 * Decide the status a respondent should get when they want to mark "going",
 * given the counts of the OTHER respondents (excluding the actor).
 * - "going" while there is room within maxPlayers
 * - "reserve" once going is full but reserves are available (max maxReserves)
 * - "full" when both going and reserves are at capacity
 */
export function resolveJoinStatus(
  othersGoingCount: number,
  othersReserveCount: number,
  maxPlayers: number,
  maxReserves = 2
): "going" | "reserve" | "full" {
  if (othersGoingCount < maxPlayers) return "going";
  if (othersReserveCount < maxReserves) return "reserve";
  return "full";
}

/** Promote earliest reserves when going count drops below maxPlayers. */
export function applyReservePromotion(
  rsvps: RsvpLike[],
  maxPlayers: number,
  _maxReserves = 2
): RsvpLike[] {
  const goingCount = countGoing(rsvps);
  const slotsAvailable = maxPlayers - goingCount;
  if (slotsAvailable <= 0) return rsvps;

  const reserves = rsvps
    .filter((r) => r.status === "reserve")
    .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""));

  const toPromote = reserves.slice(0, slotsAvailable);
  if (toPromote.length === 0) return rsvps;

  const promoteIds = new Set(toPromote.map((r) => r.id));
  return rsvps.map((r) =>
    promoteIds.has(r.id) ? { ...r, status: "going" as const } : r
  );
}

/** Mark an RSVP as out and auto-promote the first reserve if slots open. */
export function handleGoingDrop(
  rsvps: RsvpLike[],
  droppedRsvpId: string,
  maxPlayers: number,
  maxReserves = 2
): RsvpLike[] {
  const updated = rsvps.map((r) =>
    r.id === droppedRsvpId ? { ...r, status: "out" as const } : r
  );
  return applyReservePromotion(updated, maxPlayers, maxReserves);
}
